import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";
import {
  autoBindStoredReferral,
  googleLoginBoide,
  loginBoide,
  resendVerificationEmail,
  setBoideToken,
  signUpBoide,
  getBoideToken,
  getMe,
  getLastLoginUsername,
  getStoredReferral,
  getStoredTrack,
  isNotVerifiedError,
  isRememberLogin,
  setLastLoginUsername,
  setRememberLogin,
  logoutBoide,
  BoideAuthError,
} from "@/lib/boide-api";
import {
  signInWithApple,
  signInWithGoogle,
  getRedirectResult,
  getPendingOAuthProvider,
  isRedirectingError,
  firebaseAuthErrorMessage,
  type OAuthProviderName,
} from "@/lib/firebase";
import { AppleLogo } from "@/components/apple-logo";
import { errorName, trackEvent } from "@/lib/analytics";
import {
  isProfileIncomplete,
  markProfilePromptDismissed,
  refreshBoideUser,
} from "@/hooks/use-boide-user";
import { BoideSettingsDialog } from "@/components/boide-settings-dialog";
import { isInAppBrowser, openInAppBrowserWarning } from "@/lib/in-app-browser";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordField } from "@/components/auth/password-field";
import { ForgotPasswordDialog } from "@/components/auth/forgot-password-dialog";
import { ReferralCodeField, useReferralInput } from "@/components/auth/referral-code-field";
import { markReferralPromptPending } from "@/lib/referral-prompt";
import { markHasPassword, validateNewPassword } from "@/lib/password";
import { cn } from "@/lib/utils";
import logoFull from "@/assets/logo-full.webp";
import "@/components/auth/auth-page.css";

function normalizeReturnTo(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const path = value.trim();
  if (!path.startsWith("/") || path.startsWith("//") || path.startsWith("/login")) {
    return undefined;
  }
  return path;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search): { returnTo?: string } => ({
    returnTo: normalizeReturnTo(search.returnTo),
  }),
  head: () => ({
    meta: [
      { title: "Đăng Nhập Tài Khoản - Tham Vấn Vận Mệnh Bằng AI" },
      {
        name: "description",
        content:
          "Đăng nhập để gieo quẻ Kinh Dịch, lập lá số Tử Vi, Bát Tự và tham vấn vận mệnh bằng AI. Đăng nhập ngay!",
      },
    ],
  }),
  beforeLoad: async ({ search }) => {
    if (typeof window === "undefined") return;
    if (!getBoideToken()) return;
    try {
      await getMe();
    } catch (error) {
      if (error instanceof BoideAuthError) logoutBoide();
      return;
    }
    throw redirect({ to: search.returnTo || "/" });
  },
  component: LoginPage,
});

/** Nhãn hiển thị theo nhà cung cấp — dùng cho nút, toast và tên sự kiện analytics. */
const PROVIDER_LABEL: Record<OAuthProviderName, string> = {
  google: "Google",
  apple: "Apple",
};

const SIGN_IN_BY_PROVIDER: Record<OAuthProviderName, () => Promise<string>> = {
  google: signInWithGoogle,
  apple: signInWithApple,
};

/**
 * BE trả nguyên văn các hằng lỗi tiếng Anh ở `src/utils/constant/auth.py`.
 *
 * ⚠️ "Không tồn tại" và "sai mật khẩu" cố ý gộp thành MỘT câu: BE có phân biệt, nhưng
 * lặp lại sự phân biệt đó lên giao diện là biến màn đăng nhập thành công cụ dò xem
 * email nào đã đăng ký — đúng thứ mà `ctr_forgot_password` chịu khó giấu đi.
 */
function loginErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : "";
  if (/password is not correct|user is not exists/i.test(raw)) {
    return "Email/tên đăng nhập hoặc mật khẩu không đúng.";
  }
  if (/user is not active/i.test(raw)) return "Tài khoản đang bị khoá.";
  if (/đã bị xoá|đã xoá/i.test(raw)) return raw;
  return raw || "Đăng nhập thất bại, vui lòng thử lại.";
}

/** Gợi ý tên đăng nhập từ email — BE đòi `username` riêng, người dùng thì chỉ có email. */
function usernameFromEmail(email: string): string {
  return email
    .split("@")[0]
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9_.]/g, "")
    .slice(0, 32);
}

type Tab = "login" | "signup";

function LoginPage() {
  const navigate = useNavigate();
  const { returnTo } = Route.useSearch();
  const afterLoginPath = returnTo || "/";
  const afterLoginPathRef = useRef(afterLoginPath);
  const [pendingProvider, setPendingProvider] = useState<OAuthProviderName | null>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [isCheckingRedirect, setIsCheckingRedirect] = useState(true);

  const [tab, setTab] = useState<Tab>("login");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupUsername, setSignupUsername] = useState("");
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirm, setSignupConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  /**
   * Ghi nhớ đăng nhập. Khởi tạo `true` (hành vi cũ) rồi mới đọc localStorage trong
   * effect — đọc thẳng lúc render sẽ lệch SSR/hydration vì máy chủ không có storage.
   */
  const [remember, setRemember] = useState(true);
  /** Đăng ký xong: chờ người dùng bấm link trong mail, chưa có token nào để đi tiếp. */
  const [awaitingVerify, setAwaitingVerify] = useState<{ email: string } | null>(null);
  /** Đăng nhập trúng tài khoản chưa xác thực — giữ lại để bấm "gửi lại mail" được. */
  const [unverified, setUnverified] = useState<{ username: string; password: string } | null>(null);
  const [resending, setResending] = useState(false);
  /** Mã giới thiệu: tự điền + KHOÁ khi đến từ link, không thì người dùng tự gõ. */
  const referral = useReferralInput();

  // Nạp tuỳ chọn "ghi nhớ" + email lần trước. Chỉ chạy ở client (xem ghi chú ở state).
  useEffect(() => {
    setRemember(isRememberLogin());
    const last = getLastLoginUsername();
    if (last) setIdentifier(last);
  }, []);

  const changeRemember = (value: boolean) => {
    setRemember(value);
    // Ghi ngay, không đợi lúc bấm đăng nhập: luồng OAuth trên iOS rời hẳn khỏi trang
    // (redirect) rồi mới quay lại, lúc đó state này đã mất — chỉ giá trị đã lưu còn.
    setRememberLogin(value);
    if (!value) setLastLoginUsername("");
  };

  // Sau khi có token: kiểm hồ sơ rồi mới rời trang. Dùng chung cho cả OAuth lẫn
  // đăng nhập bằng mật khẩu — hai đường khác nhau ở chỗ LẤY token, không ở phần sau.
  const finishLogin = useCallback(async () => {
    // Có token rồi ⇒ gắn luôn mã giới thiệu đang giữ (từ link hoặc tự gõ), không đợi
    // lượt nạp tiền. Tài khoản đã có người giới thiệu thì BE từ chối — im lặng.
    // Đánh dấu lượt đăng nhập để `ReferralPromptDialog` (mount ở __root) mời nhập mã
    // giới thiệu khi đã rời /login — nó tự bỏ qua nếu tài khoản đã gắn mã.
    markReferralPromptPending();
    const bound = await autoBindStoredReferral();
    if (bound) toast.success(`Đã gắn mã giới thiệu ${bound}`);
    try {
      // Đi qua store `useBoideUser` thay vì gọi `getProfile()` trần: store được làm ấm
      // (me + profile) nên trang đích không phải gọi lại hai endpoint này lần nữa.
      const user = await refreshBoideUser();
      if (isProfileIncomplete(user?.profile)) {
        setProfileDialogOpen(true);
        return; // navigate sau khi user đóng dialog
      }
    } catch {
      // lỗi fetch profile → navigate luôn
    }
    void navigate({ to: afterLoginPathRef.current });
  }, [navigate]);

  // Đổi Firebase ID token lấy phiên Bói Đê. Dùng chung cho Google và Apple:
  // BE chỉ xác thực Firebase ID token, không quan tâm nhà cung cấp.
  const completeLogin = useCallback(
    async (provider: OAuthProviderName, idToken: string) => {
      const res = await googleLoginBoide(
        idToken,
        getStoredReferral() ?? undefined,
        getStoredTrack() ?? undefined,
      );
      if (!res.access_token) throw new Error("Không nhận được access_token");
      setBoideToken(res.access_token);
      trackEvent(`auth_${provider}_success`);
      toast.success(`Đăng nhập ${PROVIDER_LABEL[provider]} thành công.`);
      await finishLogin();
    },
    [finishLogin],
  );

  // Check if user is returning from Firebase redirect flow.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const idToken = await getRedirectResult();
        if (!idToken || cancelled) return;
        // Lượt redirect đã ghi tên nhà cung cấp vào localStorage trước khi rời
        // trang; đọc lại để gắn đúng nhãn và đúng tên sự kiện analytics.
        const provider = getPendingOAuthProvider() ?? "google";
        setPendingProvider(provider);
        trackEvent(`auth_${provider}_start`);
        await completeLogin(provider, idToken);
      } catch (err) {
        if (!cancelled) {
          trackEvent("auth_redirect_error", { error_name: errorName(err) });
          toast.error(err instanceof Error ? err.message : "Không thể khôi phục đăng nhập");
        }
      } finally {
        if (!cancelled) {
          setPendingProvider(null);
          setIsCheckingRedirect(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [completeLogin]);

  const handleLogin = async (provider: OAuthProviderName) => {
    if (isInAppBrowser()) {
      openInAppBrowserWarning();
      return;
    }
    // Kiểm mã TRƯỚC khi rời trang: OAuth redirect xong mới báo mã sai là quá muộn —
    // tài khoản Google/Apple mới đã tạo và `referred_by` không gắn lại được nữa.
    // Đồng bộ, không await — xem `blockingError`.
    const refError = referral.blockingError();
    if (refError) {
      toast.error(refError);
      return;
    }
    setPendingProvider(provider);
    setRememberLogin(remember);
    trackEvent(`auth_${provider}_start`);
    try {
      const idToken = await SIGN_IN_BY_PROVIDER[provider]();
      await completeLogin(provider, idToken);
    } catch (err) {
      // Redirect là lối ra hợp lệ; effect ở trên hoàn tất khi người dùng quay về.
      if (isRedirectingError(err)) return;
      trackEvent(`auth_${provider}_error`, { error_name: errorName(err) });
      toast.error(
        firebaseAuthErrorMessage(err) ??
          (err instanceof Error ? err.message : `Đăng nhập ${PROVIDER_LABEL[provider]} thất bại`),
      );
    } finally {
      setPendingProvider(null);
    }
  };

  const handlePasswordLogin = async () => {
    const username = identifier.trim();
    if (!username || !password) {
      toast.error("Vui lòng nhập đầy đủ email/tên đăng nhập và mật khẩu.");
      return;
    }
    setSubmitting(true);
    setUnverified(null);
    // Phải đặt TRƯỚC `loginBoide` — chính hàm đó ghi token, và nơi token được ghi
    // (localStorage hay sessionStorage) do tuỳ chọn này quyết định.
    setRememberLogin(remember);
    trackEvent("auth_password_start");
    try {
      await loginBoide(username, password);
      setLastLoginUsername(remember ? username : "");
      markHasPassword(username);
      trackEvent("auth_password_success");
      toast.success("Đăng nhập thành công.");
      await finishLogin();
    } catch (err) {
      // BE trả 403 + "User is not verified" — đây KHÔNG phải sai mật khẩu, và nói
      // "sai mật khẩu" ở đây sẽ đẩy người dùng đi đặt lại mật khẩu một cách vô ích.
      if (isNotVerifiedError(err)) {
        trackEvent("auth_password_unverified");
        setUnverified({ username, password });
        return;
      }
      trackEvent("auth_password_error", { error_name: errorName(err) });
      toast.error(loginErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async () => {
    const email = signupEmail.trim();
    const username = (signupUsername || usernameFromEmail(email)).trim();
    if (!email.includes("@")) {
      toast.error("Email không hợp lệ.");
      return;
    }
    if (username.length < 3) {
      toast.error("Tên đăng nhập cần ít nhất 3 ký tự.");
      return;
    }
    const error = validateNewPassword(signupPassword, signupConfirm);
    if (error) {
      toast.error(error);
      return;
    }
    const refCheck = await referral.ensureValid();
    if (!refCheck.ok) {
      toast.error(refCheck.message);
      return;
    }
    setSubmitting(true);
    trackEvent("auth_signup_start");
    try {
      await signUpBoide({
        email,
        username,
        password: signupPassword,
        referralCode: getStoredReferral() ?? undefined,
        track: getStoredTrack() ?? undefined,
      });
      markHasPassword(username);
      trackEvent("auth_signup_success");
      // 🔴 KHÔNG điều hướng vào app: BE cố ý không cấp token cho tài khoản chưa xác
      // thực email (`ctr_sign_up`), nên vào app cũng chỉ gặp 401 ngay sau đó.
      setAwaitingVerify({ email });
      setIdentifier(email);
      setPassword(signupPassword);
    } catch (err) {
      trackEvent("auth_signup_error", { error_name: errorName(err) });
      toast.error(err instanceof Error ? err.message : "Đăng ký thất bại, vui lòng thử lại.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async (username: string, pw: string) => {
    setResending(true);
    try {
      await resendVerificationEmail(username, pw);
      trackEvent("auth_resend_verification");
      toast.success("Đã gửi lại email xác thực. Kiểm tra cả mục Spam nhé.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Không gửi lại được email xác thực.");
    } finally {
      setResending(false);
    }
  };

  const handleProfileDialogChange = (open: boolean) => {
    setProfileDialogOpen(open);
    if (!open) {
      // Đóng/bỏ qua ở màn login = không nhắc lại trong suốt phiên duyệt web này.
      markProfilePromptDismissed();
      void navigate({ to: afterLoginPathRef.current });
    }
  };

  const busy = pendingProvider !== null || isCheckingRedirect || submitting;

  const getSubtitle = () => {
    if (returnTo?.startsWith("/kinh-dich")) {
      return "Đăng nhập để tiếp tục thỉnh quẻ";
    }
    if (returnTo?.startsWith("/tu-vi")) {
      return "Đăng nhập để khai mở lá số";
    }
    return "Đăng nhập để tham vấn Thiên Cơ";
  };

  return (
    <>
      <div className="auth-page min-h-0 flex-1 overflow-y-auto">
        <div className="auth-page-inner">
          <div className="auth-panel">
            <header className="auth-header">
              <Link to="/" aria-label="Bói Đê — về trang chủ" className="auth-brand">
                <img src={logoFull} alt="Bói Đê" width={88} height={88} />
              </Link>
              <h1>
                {awaitingVerify ? "Xác thực email" : tab === "login" ? "Chào mừng trở lại" : "Tạo tài khoản"}
              </h1>
              <p>
                {awaitingVerify
                  ? "Chỉ còn một bước để hoàn tất đăng ký."
                  : tab === "login"
                    ? getSubtitle()
                    : "Bắt đầu hành trình thấu hiểu bản thân."}
              </p>
            </header>

            {!awaitingVerify ? (
              <div
                role="group"
                aria-label="Đăng nhập hoặc đăng ký"
                className="auth-mode"
              >
                {(
                  [
                    ["login", "Đăng nhập"],
                    ["signup", "Đăng ký"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={tab === value}
                    onClick={() => {
                      setTab(value);
                      setUnverified(null);
                    }}
                    className={cn(
                      "auth-mode-button",
                      tab === value
                        ? "auth-mode-active"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}

            {!awaitingVerify ? (
              // Đặt TRÊN các nút Google/Apple: mã phải có sẵn trước khi bấm, vì với
              // người dùng MỚI chính lượt đăng nhập OAuth đầu tiên là lượt tạo tài khoản.
              <div className="mb-4">
                <ReferralCodeField
                  key={tab}
                  referral={referral}
                  disabled={busy}
                  defaultOpen={tab === "signup"}
                />
              </div>
            ) : null}

            <div className="auth-providers">
              <Button
                type="button"
                disabled={busy}
                aria-busy={pendingProvider === "google"}
                onClick={() => void handleLogin("google")}
                variant="outline"
                className="auth-provider"
              >
                {pendingProvider === "google" ? <Loader2 className="animate-spin" /> : <GoogleLogo />}
                <span>
                  {pendingProvider === "google" ? "Đang kết nối Google..." : "Tiếp tục với Google"}
                </span>
              </Button>

              <Button
                type="button"
                disabled={busy}
                aria-busy={pendingProvider === "apple"}
                onClick={() => void handleLogin("apple")}
                variant="outline"
                className="auth-provider"
              >
                {pendingProvider === "apple" ? <Loader2 className="animate-spin" /> : <AppleLogo />}
                <span>
                  {pendingProvider === "apple" ? "Đang kết nối Apple..." : "Tiếp tục với Apple"}
                </span>
              </Button>
            </div>

            <div className="auth-divider">
              <span className="h-px flex-1 bg-gold-soft/40" />
              <span className="text-xs text-muted-foreground">hoặc tiếp tục với email</span>
              <span className="h-px flex-1 bg-gold-soft/40" />
            </div>

            {awaitingVerify ? (
              <VerifyNotice
                email={awaitingVerify.email}
                resending={resending}
                onResend={() => void handleResendVerification(identifier, password)}
                onBackToLogin={() => {
                  setAwaitingVerify(null);
                  setTab("login");
                }}
              />
            ) : (
              <>
                {tab === "login" ? (
                  <form
                    className="auth-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void handlePasswordLogin();
                    }}
                  >
                    <div className="flex flex-col gap-1.5">
                      <Label
                        htmlFor="login-identifier"
                        className="text-[11px] uppercase tracking-wider text-primary/85"
                      >
                        Email hoặc tên đăng nhập
                      </Label>
                      <Input
                        id="login-identifier"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        autoComplete="username"
                        placeholder="Nhập email hoặc tên đăng nhập"
                        disabled={busy}
                      />
                    </div>
                    <PasswordField
                      label="Mật khẩu"
                      value={password}
                      onChange={setPassword}
                      autoComplete="current-password"
                      disabled={busy}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                      <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                        <Checkbox
                          checked={remember}
                          onCheckedChange={(value) => changeRemember(value === true)}
                          disabled={busy}
                        />
                        Ghi nhớ đăng nhập
                      </label>
                      <button
                        type="button"
                        onClick={() => setForgotOpen(true)}
                        className="min-h-9 text-xs font-medium text-primary underline-offset-4 hover:underline"
                      >
                        Quên mật khẩu?
                      </button>
                    </div>

                    {unverified ? (
                      <div className="flex flex-col gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-muted-foreground">
                        <span>
                          Tài khoản chưa xác thực email. Hãy mở link trong mail xác thực trước khi
                          đăng nhập.
                        </span>
                        <Button
                          type="button"
                          className="auth-action"
                          variant="outline"
                          disabled={resending}
                          onClick={() =>
                            void handleResendVerification(unverified.username, unverified.password)
                          }
                        >
                          {resending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                          Gửi lại email xác thực
                        </Button>
                      </div>
                    ) : null}

                    <Button type="submit" className="auth-submit" disabled={busy}>
                      {submitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                      {submitting ? "Đang đăng nhập..." : "Đăng nhập"}
                      {!submitting ? <ArrowRight aria-hidden="true" /> : null}
                    </Button>
                  </form>
                ) : (
                  <form
                    className="auth-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void handleSignUp();
                    }}
                  >
                    <div className="flex flex-col gap-1.5">
                      <Label
                        htmlFor="signup-email"
                        className="text-[11px] uppercase tracking-wider text-primary/85"
                      >
                        Email
                      </Label>
                      <Input
                        id="signup-email"
                        type="email"
                        value={signupEmail}
                        onChange={(e) => {
                          setSignupEmail(e.target.value);
                          // Chỉ gợi ý khi người dùng chưa tự sửa — sửa rồi mà vẫn bị
                          // ghi đè theo email là kiểu khó chịu điển hình của form.
                          if (!usernameTouched)
                            setSignupUsername(usernameFromEmail(e.target.value));
                        }}
                        autoComplete="email"
                        placeholder="Nhập địa chỉ email"
                        disabled={busy}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label
                        htmlFor="signup-username"
                        className="text-[11px] uppercase tracking-wider text-primary/85"
                      >
                        Tên đăng nhập
                      </Label>
                      <Input
                        id="signup-username"
                        value={signupUsername}
                        onChange={(e) => {
                          setUsernameTouched(true);
                          setSignupUsername(e.target.value);
                        }}
                        autoComplete="username"
                        placeholder="Chọn tên đăng nhập"
                        disabled={busy}
                      />
                    </div>
                    <PasswordField
                      label="Mật khẩu"
                      value={signupPassword}
                      onChange={setSignupPassword}
                      autoComplete="new-password"
                      disabled={busy}
                      showStrength
                      hint="Ít nhất 8 ký tự, có cả chữ và số, không dấu tiếng Việt."
                    />
                    <PasswordField
                      label="Nhập lại mật khẩu"
                      value={signupConfirm}
                      onChange={setSignupConfirm}
                      autoComplete="new-password"
                      disabled={busy}
                    />
                    <Button type="submit" className="auth-submit" disabled={busy}>
                      {submitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                      {submitting ? "Đang tạo tài khoản..." : "Tạo tài khoản"}
                      {!submitting ? <ArrowRight aria-hidden="true" /> : null}
                    </Button>
                  </form>
                )}
              </>
            )}

            <p className="mt-6 text-center text-xs leading-5 text-muted-foreground">
              Khi tiếp tục, bạn xác nhận đã đọc{" "}
              <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">
                Điều khoản sử dụng
              </Link>{" "}
              và{" "}
              <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
                Chính sách bảo mật
              </Link>
              .
            </p>
          </div>
        </div>
      </div>

      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        defaultUsername={identifier}
      />

      <BoideSettingsDialog
        firstTime
        open={profileDialogOpen}
        onOpenChange={handleProfileDialogChange}
      />
    </>
  );
}

/**
 * Màn "đã đăng ký, chờ xác thực email".
 *
 * 🔴 Đây là điểm dừng BẮT BUỘC của luồng đăng ký: BE tạo tài khoản rồi gửi mail, và
 * `/login` chặn bằng `email_verified` cho tới khi người dùng bấm link. Nhảy thẳng vào
 * app sau khi đăng ký chỉ dẫn tới một chuỗi 401.
 */
function VerifyNotice({
  email,
  resending,
  onResend,
  onBackToLogin,
}: {
  email: string;
  resending: boolean;
  onResend: () => void;
  onBackToLogin: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex gap-3 rounded-lg border border-gold-soft/60 bg-background/40 p-4">
        <MailCheck className="mt-0.5 size-5 shrink-0 text-primary" />
        <div className="min-w-0 space-y-1.5 text-sm">
          <p className="font-semibold text-foreground">Kiểm tra hộp thư của bạn</p>
          <p className="text-muted-foreground">
            Chúng tôi đã gửi link xác thực tới <strong className="break-all text-foreground">{email}</strong>.
            Bấm link đó rồi quay lại đăng nhập.
          </p>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Không thấy mail? Kiểm tra mục Spam / Quảng cáo trước khi gửi lại.
      </p>
      <div className="flex flex-col gap-3">
        <Button variant="outline" className="auth-action" disabled={resending} onClick={onResend}>
          {resending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
          Gửi lại email
        </Button>
        <Button className="auth-action" onClick={onBackToLogin}>
          Tôi đã xác thực — Đăng nhập
        </Button>
      </div>
    </div>
  );
}

/** Google provider mark; the browser Chrome icon is not a Google sign-in mark. */
function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.39-.18-2.05H12v3.88h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.36Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.61-2.41l-3.24-2.51c-.9.6-2.05.96-3.37.96-2.6 0-4.8-1.76-5.59-4.12H3.07v2.59A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.41 13.92a6 6 0 0 1 0-3.84V7.49H3.07a10 10 0 0 0 0 9.02l3.34-2.59Z" />
      <path fill="#EA4335" d="M12 5.96c1.47 0 2.79.5 3.82 1.49l2.86-2.87A9.6 9.6 0 0 0 12 2a10 10 0 0 0-8.93 5.49l3.34 2.59C7.2 7.72 9.4 5.96 12 5.96Z" />
    </svg>
  );
}
